import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Maximize, Settings2 } from "lucide-react";
import { useAuth } from "@/App";
import { useScreenFeed, GOAL_TYPES } from "@/lib/bigScreen";
import { Stage, Scoreboard, IdleScreen } from "@/components/bigscreen/Scoreboard";
import { GoalCelebration, EventBanner, LineupsView, StatsView, BreakView, UpcomingView, StandingsView, SlidesView } from "@/components/bigscreen/ScreenViews";

const ANNOUNCE = [...GOAL_TYPES, "yellow_card", "red_card", "substitution"];
const BANNER_SECONDS = 7;
const MAX_EVENT_AGE_S = 180; // never celebrate old events discovered late (e.g. after a long outage)

// Tracks which events this screen already announced (persisted → no replay after refresh/reconnect)
const useAnnouncements = (centre, celebrationSeconds, skewMs) => {
  const [current, setCurrent] = useState(null);
  const queue = useRef([]);
  const seenKey = centre ? `bs-seen-${centre.match.match_id}` : null;

  useEffect(() => {
    if (!centre) return;
    const events = centre.events.filter((e) => ANNOUNCE.includes(e.type));
    const stored = localStorage.getItem(seenKey);
    const seen = new Set(stored ? JSON.parse(stored) : []);
    const ids = new Set(events.map((e) => e.event_id));
    if (stored) {
      const nowMs = Date.now() + skewMs.current;
      events.filter((e) => !seen.has(e.event_id)).forEach((e) => {
        const age = (nowMs - new Date(e.created_at).getTime()) / 1000;
        if (age >= MAX_EVENT_AGE_S) return;
        if (GOAL_TYPES.includes(e.type)) queue.current.unshift(e); else queue.current.push(e);
      });
      // Goals pre-empt a card/substitution banner that is on screen
      if (queue.current[0] && GOAL_TYPES.includes(queue.current[0].type)) {
        setCurrent((cur) => (cur && !GOAL_TYPES.includes(cur.type) ? null : cur));
      }
    }
    // Corrections: drop anything that no longer exists
    queue.current = queue.current.filter((e) => ids.has(e.event_id));
    setCurrent((cur) => (cur && !ids.has(cur.event_id) ? null : cur));
    localStorage.setItem(seenKey, JSON.stringify([...ids]));
  }, [centre, seenKey, skewMs]);

  useEffect(() => {
    if (current) {
      const secs = GOAL_TYPES.includes(current.type) ? celebrationSeconds : BANNER_SECONDS;
      const t = setTimeout(() => setCurrent(null), secs * 1000);
      return () => clearTimeout(t);
    }
    const iv = setInterval(() => {
      if (queue.current.length) setCurrent(queue.current.shift());
    }, 300);
    return () => clearInterval(iv);
  }, [current, celebrationSeconds]);

  return current;
};

// Operator-triggered "replay goal" — shown once, only within its window
const useReplay = (display, centre, skewMs) => {
  const [ev, setEv] = useState(null);
  const shown = useRef(null);
  useEffect(() => {
    if (!display?.replay_at || !centre || shown.current === display.replay_at) return undefined;
    const ageMs = Date.now() + skewMs.current - new Date(display.replay_at).getTime();
    const secs = display.celebration_seconds || 12;
    const goal = centre.events.find((e) => e.event_id === display.replay_goal_event_id);
    if (!goal || ageMs > secs * 1000) return undefined;
    shown.current = display.replay_at;
    setEv(goal);
    const t = setTimeout(() => setEv(null), secs * 1000 - Math.max(0, ageMs));
    return () => clearTimeout(t);
  }, [display, centre, skewMs]);
  return ev;
};

const MainView = ({ snap, skewMs, tick, stale }) => {
  const { display, centre, upcoming, standings, slides } = snap;
  const mode = display.mode;
  if (mode === "slides" && slides.length) return <SlidesView slides={slides} seconds={display.slide_seconds} />;
  if (mode === "upcoming") return <UpcomingView upcoming={upcoming} />;
  if (!centre) return slides.length ? <SlidesView slides={slides} seconds={display.slide_seconds} /> : <IdleScreen />;
  if (mode === "lineups") return <LineupsView centre={centre} />;
  if (mode === "stats") return <StatsView centre={centre} />;
  if (mode === "standings") return <StandingsView centre={centre} rows={standings} />;
  if (mode === "halftime") return <BreakView centre={centre} kind="halftime" />;
  const finished = centre.match.status === "completed" || centre.clock?.clock_state === "full_time";
  if (mode === "fulltime") return finished ? <BreakView centre={centre} kind="fulltime" /> : <Scoreboard centre={centre} skewMs={skewMs} tick={tick} stale={stale} />;
  // Scoreboard auto-presents HT / FT at the right stage
  if (centre.clock?.clock_state === "half_time") return <BreakView centre={centre} kind="halftime" />;
  if (finished) return <BreakView centre={centre} kind="fulltime" />;
  return <Scoreboard centre={centre} skewMs={skewMs} tick={tick} stale={stale} />;
};

const ScreenChrome = ({ isOperator, status, stale, lastMessageAt }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    let t;
    const show = () => { setVisible(true); clearTimeout(t); t = setTimeout(() => setVisible(false), 3000); };
    window.addEventListener("mousemove", show);
    return () => { window.removeEventListener("mousemove", show); clearTimeout(t); };
  }, []);
  const fullscreen = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.())?.catch?.(() => {});
  const problem = status !== "live" || stale;
  return (
    <>
      <div className={`fixed top-4 right-4 z-50 flex gap-2 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <button onClick={fullscreen} className="flex items-center gap-2 px-4 py-2 bg-black/70 text-white text-sm font-semibold hover:bg-black" data-testid="bs-fullscreen-btn">
          <Maximize className="w-4 h-4" /> Fullscreen
        </button>
        {isOperator && (
          <Link to="/big-screen" className="flex items-center gap-2 px-4 py-2 bg-[#FFC72C] text-black text-sm font-semibold" data-testid="bs-control-link">
            <Settings2 className="w-4 h-4" /> Control panel
          </Link>
        )}
      </div>
      {problem && (
        <div className="fixed bottom-3 right-3 z-50 flex items-center gap-2 px-3 py-1.5 bg-black/70 text-xs text-white" data-testid="bs-connection-warning">
          <span className={`w-2 h-2 rounded-full ${stale || status === "offline" ? "bg-red-500" : "bg-amber-400 animate-pulse"}`} />
          {isOperator
            ? `${stale ? "Connection lost — showing last confirmed data" : "Reconnecting…"}${lastMessageAt ? ` · last update ${new Date(lastMessageAt).toLocaleTimeString()}` : ""}`
            : null}
        </div>
      )}
    </>
  );
};

const BigScreenPage = () => {
  const { matchId, screenId } = useParams();
  const target = matchId ? `match-${matchId}` : screenId || "stadium";
  const { user } = useAuth();
  const isOperator = ["admin", "big_screen_operator"].includes(user?.role);
  const { snap, status, stale, lastMessageAt, error, skewMs } = useScreenFeed(target);
  const [tick, setTick] = useState(0);
  const centre = snap?.centre;
  const announcement = useAnnouncements(centre, snap?.display?.celebration_seconds || 12, skewMs);
  const replay = useReplay(snap?.display, centre, skewMs);

  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(iv);
  }, []);

  const guard = useCallback((e) => { if (document.fullscreenElement) { e.preventDefault(); e.returnValue = ""; } }, []);
  useEffect(() => {
    document.title = "ABFA Big Screen";
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [guard]);

  const goal = replay || (announcement && GOAL_TYPES.includes(announcement.type) ? announcement : null);
  const banner = !goal && announcement && !GOAL_TYPES.includes(announcement.type) ? announcement : null;

  return (
    <div data-testid="big-screen-page" data-connection={status} data-stale={stale ? "1" : "0"}>
      <Stage>
        {error && !snap ? <IdleScreen message="Screen unavailable" /> : snap ? <MainView snap={snap} skewMs={skewMs} tick={tick} stale={stale} /> : <IdleScreen />}
        {centre && goal && <GoalCelebration centre={centre} event={goal} />}
        {centre && banner && <EventBanner centre={centre} event={banner} />}
      </Stage>
      <ScreenChrome isOperator={isOperator} status={status} stale={stale} lastMessageAt={lastMessageAt} />
    </div>
  );
};

export default BigScreenPage;
